/**
 * D439 step 6: the settings switch, 46 by 28 and round, in place of the boxed On and Off pair.
 * The button is the 44px-or-more tap target (58 by 48); the track is drawn inside it. On is the
 * accent with `accent-ink` for the thumb; off (D458) is no fill, a 2px `ink-2` ring and an
 * 18px `ink-2` knob at the left (5.5:1 on the dark surface, 5.1:1 on the light). A switch that cannot be turned on greys itself (D235) and says why through `reason`.
 */
export const SWITCH_TRACK = { width: 46, height: 28 } as const;
/** D458: inside the 2px ring the track is 42 by 24. Off: an 18px knob at the left; on: 22px at the right. */
export const SWITCH_KNOB_OFF = { size: 18, top: 3, left: 3 } as const;
export const SWITCH_KNOB_ON = { size: 22, top: 1, left: 19 } as const;
export const SWITCH_TARGET = { width: 58, height: 48 } as const;

export function Switch({
  on,
  onChange,
  label,
  disabled = false,
  reason,
  testId,
}: {
  on: boolean;
  onChange: (on: boolean) => void;
  /** The accessible name: the row's label. */
  label: string;
  disabled?: boolean;
  reason?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-disabled={disabled || undefined}
      title={disabled ? reason : undefined}
      data-testid={testId}
      onClick={() => {
        if (!disabled) onChange(!on);
      }}
      style={{
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        width: SWITCH_TARGET.width,
        height: SWITCH_TARGET.height,
        margin: '0 -6px 0 0',
        padding: 0,
        border: 0,
        background: 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          boxSizing: 'border-box',
          width: SWITCH_TRACK.width,
          height: SWITCH_TRACK.height,
          borderRadius: 14,
          // D458: off is no fill and a 2px ring, on is the accent fill; the border is 2px in
          // both so the track never shifts. The shape changes too (ring to fill, small knob to
          // large), so it reads without colour.
          border: `2px solid ${disabled ? 'var(--off-line)' : on ? 'var(--switch-ring, var(--accent))' : 'var(--ink-2)'}`,
          background: disabled
            ? 'var(--off-bg)'
            : on
              ? 'var(--switch-on, var(--accent))'
              : 'transparent',
          transition: 'background 120ms',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: on ? SWITCH_KNOB_ON.top : SWITCH_KNOB_OFF.top,
            left: on ? SWITCH_KNOB_ON.left : SWITCH_KNOB_OFF.left,
            width: on ? SWITCH_KNOB_ON.size : SWITCH_KNOB_OFF.size,
            height: on ? SWITCH_KNOB_ON.size : SWITCH_KNOB_OFF.size,
            borderRadius: '50%',
            background: disabled
              ? 'var(--off-ink)'
              : on
                ? 'var(--switch-knob, var(--accent-ink))'
                : 'var(--ink-2)',
            transition: 'left 120ms, top 120ms, width 120ms, height 120ms',
          }}
        />
      </span>
    </button>
  );
}
